import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { 
  Copy, 
  Check, 
  Terminal,
  Anchor,
  Wifi,
  Server,
  ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';
import { copyToClipboard } from '@/utils/clipboard';

const API = process.env.REACT_APP_BACKEND_URL;

const IoTSignupPage = () => {
  const [claimCode, setClaimCode] = useState('');
  const [copied, setCopied] = useState(false);
  const [showCommand, setShowCommand] = useState(false);

  const handleCodeChange = (e) => {
    // Permitir apenas caracteres alfanuméricos e limitar a 6 caracteres
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6);
    setClaimCode(value);
    setShowCommand(false);
  };

  const handleShowCommand = () => {
    if (claimCode.length !== 6) {
      toast.error('Digite um código de 6 caracteres');
      return;
    }
    setShowCommand(true);
  };

  const handleCopy = async () => {
    const command = `curl -sSL "${API}/api/provisioning/download-agent?code=${claimCode}" | sudo python3 -`;
    const success = await copyToClipboard(command);
    if (success) {
      setCopied(true);
      toast.success('Comando copiado!');
      setTimeout(() => setCopied(false), 3000);
    } else {
      toast.info('Selecione o comando e use Ctrl+C para copiar');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-xl bg-slate-800/50 border-slate-700 backdrop-blur">
        <CardHeader className="text-center space-y-4">
          {/* Logo */}
          <div className="flex justify-center">
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-lg shadow-cyan-500/20">
              <Anchor className="w-10 h-10 text-white" />
            </div>
          </div>
          
          <div>
            <CardTitle className="text-2xl text-white">Octopus IoT</CardTitle>
            <CardDescription className="text-slate-400">
              Provisionamento de Equipamento
            </CardDescription>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Instruções */}
          <div className="bg-slate-700/30 rounded-lg p-4 space-y-2">
            <h3 className="text-sm font-medium text-slate-300 flex items-center gap-2">
              <Server className="w-4 h-4" />
              Como provisionar
            </h3>
            <ol className="text-sm text-slate-400 space-y-1 list-decimal list-inside">
              <li>Digite o código de 6 dígitos fornecido pelo administrador</li>
              <li>Copie o comando gerado</li>
              <li>Cole e execute no terminal do Raspberry Pi</li>
            </ol>
          </div>

          {/* Input do código */}
          <div className="space-y-2">
            <Label htmlFor="claim-code" className="text-slate-300">
              Código de Provisionamento
            </Label>
            <div className="flex gap-2">
              <Input
                id="claim-code"
                placeholder="XXXXXX"
                value={claimCode}
                onChange={handleCodeChange}
                className="text-center text-2xl font-mono tracking-widest bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500"
                maxLength={6}
                data-testid="claim-code-input"
              />
              <Button 
                onClick={handleShowCommand}
                disabled={claimCode.length !== 6}
                className="bg-cyan-600 hover:bg-cyan-700"
                data-testid="show-command-btn"
              >
                <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-xs text-slate-500 text-center">
              {claimCode.length}/6 caracteres
            </p>
          </div>

          {/* Comando gerado */}
          {showCommand && (
            <div className="space-y-3 animate-in fade-in slide-in-from-top-2 duration-300">
              <div className="flex items-center gap-2">
                <Terminal className="w-4 h-4 text-cyan-400" />
                <span className="text-sm text-slate-300">Comando para o Raspberry Pi</span>
                <Badge variant="outline" className="text-cyan-400 border-cyan-400/30">
                  <Wifi className="w-3 h-3 mr-1" />
                  Pronto
                </Badge>
              </div>
              
              <div className="bg-slate-900 rounded-lg p-4 border border-slate-700">
                <code 
                  className="text-sm text-green-400 font-mono break-all block select-all cursor-pointer"
                  onClick={(e) => {
                    const range = document.createRange();
                    range.selectNodeContents(e.target);
                    const selection = window.getSelection();
                    selection.removeAllRanges();
                    selection.addRange(range);
                  }}
                  data-testid="provision-command"
                >
                  curl -sSL "{API}/api/provisioning/download-agent?code={claimCode}" | sudo python3 -
                </code>
              </div>

              <Button 
                onClick={handleCopy}
                className="w-full bg-green-600 hover:bg-green-700"
                data-testid="copy-command-btn"
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Copiado!
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4 mr-2" />
                    Copiar Comando
                  </>
                )}
              </Button>

              <p className="text-xs text-slate-500 text-center">
                Clique no comando para selecionar, depois copie com Ctrl+C
              </p>
            </div>
          )}

          {/* Footer */}
          <div className="pt-4 border-t border-slate-700">
            <p className="text-xs text-slate-500 text-center">
              Octopus IoT Platform &copy; {new Date().getFullYear()} Areia Engenharia
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default IoTSignupPage;
